import { WEBHOOK_EVENT_LABELS, WEBHOOK_EVENTS, type WebhookEvent } from '@performance-crm/shared';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRetryDelivery,
  useRotateWebhookSecret,
  useWebhookDeliveries,
  useWebhookSubscriptions,
} from '@/lib/queries';
import { CopyButton } from './IngestPage';

export function WebhooksTab({ companyId }: { companyId: string }) {
  const { data: subs } = useWebhookSubscriptions(companyId);
  const { data: deliveries } = useWebhookDeliveries(companyId);
  const create = useCreateWebhook();
  const remove = useDeleteWebhook();
  const retry = useRetryDelivery();
  const rotateSecret = useRotateWebhookSecret();
  const toast = useToast();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([
    'lead.created',
    'lead.assigned',
    'task.handoff',
  ]);
  const [secret, setSecret] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await create.mutateAsync({ companyId, name, url, events });
      setSecret(r.secret ?? null);
      setName('');
      setUrl('');
      toast.success('Suscripción creada', 'Guarda el secreto: solo se muestra una vez.');
    } catch (err) {
      toast.error('No se pudo crear', (err as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Webhooks salientes</CardTitle>
          <CardDescription>
            El CRM avisa al multiagente (u otros sistemas) con POST JSON firmado (cabecera
            X-CRM-Signature = sha256 HMAC del cuerpo). Reintentos exponenciales, máximo 5.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="wh-name">Nombre</Label>
              <Input
                id="wh-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Multiagente WhatsApp"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="wh-url">URL</Label>
              <Input
                id="wh-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                placeholder="https://bot.miagentedigital.com/crm/webhook"
              />
            </div>
            <div className="md:col-span-3">
              <Label>Eventos</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {WEBHOOK_EVENTS.map((ev) => (
                  <label
                    key={ev}
                    className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${events.includes(ev) ? 'border-primary bg-primary/10' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={events.includes(ev)}
                      onChange={() =>
                        setEvents((s) => (s.includes(ev) ? s.filter((x) => x !== ev) : [...s, ev]))
                      }
                      className="h-3 w-3"
                    />
                    {WEBHOOK_EVENT_LABELS[ev]}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end md:col-span-3">
              <Button type="submit" disabled={create.isPending || events.length === 0}>
                <Plus className="h-4 w-4" /> Crear suscripción
              </Button>
            </div>
          </form>
          {secret && (
            <p
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
              data-testid="webhook-secret"
            >
              Secreto (solo ahora): <code className="font-mono">{secret}</code>{' '}
              <CopyButton value={secret} />
            </p>
          )}
        </CardContent>
      </Card>
      <div className="grid gap-3 md:grid-cols-2">
        {subs?.map((s) => (
          <Card key={s.id} data-testid="webhook-card">
            <CardContent className="space-y-1 p-4 text-sm">
              <div className="flex items-center justify-between">
                <p className="font-medium">
                  {s.name} {!s.active && <Badge tone="muted">inactiva</Badge>}
                </p>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-destructive"
                  aria-label="Borrar"
                  onClick={() =>
                    confirm('¿Borrar la suscripción y sus entregas?') && remove.mutate(s.id)
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-muted-foreground truncate text-xs">{s.url}</p>
              <p className="flex flex-wrap gap-1">
                {s.events.map((e) => (
                  <Badge key={e} tone="outline">
                    {e}
                  </Badge>
                ))}
              </p>
              <p className="text-muted-foreground text-xs">
                secreto {s.secretPrefix}…{' '}
                <button
                  type="button"
                  className="text-primary underline"
                  onClick={() =>
                    confirm(
                      '¿Rotar el secreto? Las entregas nuevas irán firmadas con el secreto nuevo.',
                    ) &&
                    rotateSecret
                      .mutateAsync(s.id)
                      .then((r) => {
                        setSecret(r.secret);
                        toast.success('Secreto rotado', 'Cópialo ahora: solo se muestra una vez.');
                      })
                      .catch((e: Error) => toast.error('No se pudo rotar', e.message))
                  }
                >
                  rotar
                </button>{' '}
                · última entrega {s.lastDeliveryAt ? fmt.ago(s.lastDeliveryAt) : 'nunca'}
                {s.failing > 0 && (
                  <Badge tone="danger" className="ml-1">
                    {s.failing} fallidas
                  </Badge>
                )}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
      {deliveries && deliveries.length > 0 && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Suscripción</th>
                <th className="px-3 py-2">Evento</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Intentos</th>
                <th className="px-3 py-2">Detalle</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id} className="border-t align-top">
                  <td className="whitespace-nowrap px-3 py-2">{fmt.dateTime(d.createdAt)}</td>
                  <td className="px-3 py-2">{d.subscriptionName}</td>
                  <td className="px-3 py-2">{d.event}</td>
                  <td className="px-3 py-2">
                    <Badge
                      tone={
                        d.status === 'ENTREGADO'
                          ? 'success'
                          : d.status === 'FALLIDO'
                            ? 'danger'
                            : 'muted'
                      }
                    >
                      {d.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {d.attempts}
                    {d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
                  </td>
                  <td className="max-w-md px-3 py-2">
                    {d.lastError && <p className="text-danger text-xs">{d.lastError}</p>}
                    <button
                      type="button"
                      className="text-primary text-xs underline"
                      onClick={() => setOpen(open === d.id ? null : d.id)}
                    >
                      {open === d.id ? 'ocultar' : 'ver payload'}
                    </button>
                    {open === d.id && (
                      <pre className="bg-muted mt-1 max-h-40 overflow-auto rounded p-2 text-[11px]">
                        {JSON.stringify(d.payload, null, 2)}
                      </pre>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {d.status !== 'ENTREGADO' && (
                      <Button size="sm" variant="outline" onClick={() => retry.mutate(d.id)}>
                        <RotateCcw className="h-3 w-3" /> Reintentar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
